import React, { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { departmentsApi, peopleApi, aiUseCaseApi } from '../../../services/api';
import { aiUseCaseKeys } from '../aiUseCaseKeys';
import { isRichTextEmpty } from '../../../components/RichTextField';
import { AI_TYPE_LABELS, RACI_ROLES, DATA_CATEGORY_LABELS, DATA_SENSITIVITY_LABELS } from '../../../constants/aiGovernance';
import {
  AI_ACT_CLASSIFICATION_LABELS, AI_ACT_CLASSIFICATION_TONE, AI_ACT_CLASSIFICATION_ICONS,
  ACT_ROLE_LABELS, GPAI_TIER_LABELS, gpaiTier, HUMAN_OVERSIGHT_CONTROLS, hasAnyOversight,
} from '../../../constants/aiAct';
import type { AiType, AiActClassification, ActRole, AiUseCase } from '../../../types/models';
import type { StepProps } from './types';

// A small, deliberately non-Bedrock nudge: real test-tenant data already has several near-
// duplicate use cases (e.g. three separate "Claude Code ..." submissions, and a "jira service
// desk" one drafted twice under different names) that a Bedrock coverage pass would have no
// reason to catch on its own (that pass runs at review time, per use case, and never compares
// use cases against each other). Token-overlap on the name is enough to flag "you may want to
// check for an existing one first" - a soft hint the submitter judges, not a hard block.
const STOPWORDS = new Set([
  'the', 'a', 'an', 'and', 'or', 'for', 'to', 'of', 'in', 'on', 'with', 'using', 'via',
  'ai', 'use', 'case', 'system', 'app', 'tool', 'assistant',
]);

function nameTokens(name: string): Set<string> {
  return new Set(
    (name || '')
      .toLowerCase()
      .replace(/[^a-z0-9\s]/g, ' ')
      .split(/\s+/)
      .filter((t) => t.length >= 3 && !STOPWORDS.has(t) && !/^\d+$/.test(t)),
  );
}

/** Overlap coefficient (shared / smaller set) rather than Jaccard - a short name that is
 * entirely contained in a longer one (e.g. "Claude Code" inside a longer variant) should
 * still read as a strong match, which Jaccard would under-score. */
function nameSimilarity(a: Set<string>, b: Set<string>): { score: number; shared: number } {
  let shared = 0;
  a.forEach((t) => { if (b.has(t)) shared += 1; });
  return { score: a.size && b.size ? shared / Math.min(a.size, b.size) : 0, shared };
}

// Read-only display helpers, mirroring the private ones AiUseCaseDetail.tsx already uses for
// its own read-only rendering (not imported from there since those aren't exported, and this
// step only needs a handful of them).
const Dash: React.FC = () => <span className="np-sub">—</span>;
const NotProvided: React.FC<{ label?: string }> = ({ label = 'Not answered' }) => (
  <span className="np-sub fst-italic">{label}</span>
);
const FieldLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="np-hint fw-semibold text-uppercase mt-0 mb-1" style={{ letterSpacing: '0.05em' }}>{children}</div>
);
const LabelValue: React.FC<{ label: string; value?: React.ReactNode }> = ({ label, value }) => (
  <div>
    <FieldLabel>{label}</FieldLabel>
    <div>{value || <NotProvided />}</div>
  </div>
);
const HtmlPreview: React.FC<{ value: string }> = ({ value }) => (
  isRichTextEmpty(value) ? <NotProvided /> : <div dangerouslySetInnerHTML={{ __html: value }} />
);
const SectionCard: React.FC<{ title: string; icon: string; children: React.ReactNode }> = ({ title, icon, children }) => (
  <div className="np-card mb-4">
    <div className="np-card-head"><h2><i className={icon}></i> {title}</h2></div>
    <div className="np-card-body">{children}</div>
  </div>
);
const YesNo: React.FC<{ value: boolean }> = ({ value }) => (
  <span className={`np-chip np-tone-${value ? 'success' : 'neutral'}`}>
    <i className={value ? 'ri-check-line' : 'ri-close-line'}></i>{value ? 'Yes' : 'No'}
  </span>
);

type PersonLike = { personId?: string; firstName?: string; lastName?: string; personName?: string };

// The real People-register records carry firstName/lastName (confirmed live against the
// DynamoDB table) - personName is a legacy/fallback shape some older code still assumes.
const getPersonLabel = (p: PersonLike): string => {
  const full = `${p.firstName || ''} ${p.lastName || ''}`.trim();
  return (full || p.personName || p.personId || '').trim();
};

const Step9Review: React.FC<StepProps> = ({ useCase }) => {
  const { data: people = [] } = useQuery<PersonLike[]>({ queryKey: ['people'], queryFn: peopleApi.getAll });
  const { data: departments = [] } = useQuery<Array<{ departmentId?: string; name?: string }>>({
    queryKey: ['departments'],
    queryFn: departmentsApi.getAll,
  });
  const { data: allUseCases = [] } = useQuery<AiUseCase[]>({
    queryKey: aiUseCaseKeys.collection(),
    queryFn: aiUseCaseApi.list,
  });

  const possibleDuplicates = useMemo(() => {
    const tokens = nameTokens(useCase.name);
    if (tokens.size < 2) return [];
    return allUseCases
      .filter((u) => u.useCaseId && u.useCaseId !== useCase.useCaseId)
      .map((u) => ({ match: u, ...nameSimilarity(tokens, nameTokens(u.name)) }))
      .filter((m) => m.shared >= 2 && m.score >= 0.3)
      .sort((a, b) => b.score - a.score)
      .slice(0, 3);
  }, [allUseCases, useCase.name, useCase.useCaseId]);

  const personName = (id: string): string => {
    if (!id) return '';
    const match = people.find((p) => p.personId === id);
    return match ? getPersonLabel(match) : '';
  };
  const departmentName = useMemo(
    () => departments.find((d) => d.departmentId === useCase.departmentId)?.name || '',
    [departments, useCase.departmentId],
  );

  const tier = (useCase.aiActClassification || '') as AiActClassification;
  const gpai = gpaiTier(useCase);
  const oversightAnswered = hasAnyOversight(useCase.humanOversight);
  const dataFields = useCase.dataFields || [];
  const vendors = useCase.vendors || [];
  const transfers = useCase.transfers || [];

  return (
    <div>
      <div className="np-banner np-tone-info mb-4">
        <i className="ri-information-line"></i>
        Review everything below before submitting. Once submitted, this goes to your AI governance
        reviewer for approval — check it now rather than after.
      </div>

      {possibleDuplicates.length > 0 && (
        <div className="np-banner np-tone-warning mb-4">
          <i className="ri-git-merge-line"></i>
          <span>
            <strong>This name looks similar to {possibleDuplicates.length === 1 ? 'an existing use case' : 'existing use cases'}:</strong>{' '}
            {possibleDuplicates.map((m, i) => (
              <React.Fragment key={m.match.useCaseId}>
                {i > 0 && ', '}
                <a href={`/ai-use-cases/${m.match.useCaseId}`} target="_blank" rel="noopener noreferrer">
                  {m.match.name || m.match.useCaseId}
                </a>
              </React.Fragment>
            ))}
            . Worth a quick check that this isn't already covered before submitting a new one.
          </span>
        </div>
      )}

      <SectionCard title="Overview" icon="ri-file-list-3-line">
        <div className="row g-3">
          <div className="col-12 col-md-4"><LabelValue label="Name" value={useCase.name} /></div>
          <div className="col-12 col-md-4">
            <LabelValue label="AI Type" value={AI_TYPE_LABELS[useCase.aiType as AiType]} />
          </div>
          <div className="col-12 col-md-4"><LabelValue label="Department" value={departmentName} /></div>
        </div>
      </SectionCard>

      <SectionCard title="AI Act Classification" icon="ri-scales-3-line">
        <div className="d-flex align-items-center gap-2 flex-wrap mb-3">
          <span className={`np-chip np-tone-${AI_ACT_CLASSIFICATION_TONE[tier]}`}>
            <i className={AI_ACT_CLASSIFICATION_ICONS[tier]}></i>{AI_ACT_CLASSIFICATION_LABELS[tier]}
          </span>
          {useCase.actRole && (
            <span className="np-chip np-tone-neutral">{ACT_ROLE_LABELS[useCase.actRole as ActRole]}</span>
          )}
          {gpai !== 'none' && <span className="np-chip np-tone-warning">{GPAI_TIER_LABELS[gpai]}</span>}
        </div>
        <LabelValue label="Classification rationale" value={<HtmlPreview value={useCase.classificationRationale} />} />
      </SectionCard>

      <SectionCard title="RACI" icon="ri-team-line">
        <div className="np-row2">
          {RACI_ROLES.map((role) => {
            const raw = (useCase[role.field] as string) || '';
            const display = role.personPicker ? (personName(raw) || raw) : raw;
            return <LabelValue key={role.field} label={`${role.label} (${role.raci})`} value={display} />;
          })}
        </div>
      </SectionCard>

      <SectionCard title="Data & Privacy" icon="ri-shield-user-line">
        {dataFields.length === 0 ? (
          <NotProvided label="No data fields recorded" />
        ) : (
          <div className="np-table-wrap">
            <table className="np-table">
              <thead>
                <tr><th>Field</th><th>Category</th><th>Personal data?</th><th>Special category?</th></tr>
              </thead>
              <tbody>
                {dataFields.map((f) => (
                  <tr key={f.id}>
                    <td>{f.name || <Dash />}</td>
                    <td>{DATA_CATEGORY_LABELS[f.category] || f.category || <Dash />}</td>
                    <td>{DATA_SENSITIVITY_LABELS[f.isPersonalData] || f.isPersonalData || <Dash />}</td>
                    <td><YesNo value={!!f.isSpecialCategory} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </SectionCard>

      <SectionCard title="Vendors & Transfers" icon="ri-building-4-line">
        {vendors.length === 0 ? (
          <NotProvided label="No vendors recorded" />
        ) : (
          <ul className="mb-0">
            {vendors.map((v) => (
              <li key={v.id}>
                {v.name || 'Unnamed vendor'}
                {v.modelName ? ` — ${v.modelName}` : ''}
                {v.hostingRegion ? ` (${v.hostingRegion})` : ''}
              </li>
            ))}
          </ul>
        )}
        {transfers.length > 0 && (
          <p className="np-hint mt-2 mb-0">
            {transfers.length} international transfer{transfers.length === 1 ? '' : 's'} recorded.
          </p>
        )}
      </SectionCard>

      <SectionCard title="Human Oversight" icon="ri-eye-line">
        {!oversightAnswered ? (
          <NotProvided label="No oversight controls recorded" />
        ) : (
          <div className="d-flex flex-column gap-2 mb-3">
            {HUMAN_OVERSIGHT_CONTROLS.map((c) => (
              <div key={c.field} className="d-flex align-items-center gap-2">
                <YesNo value={!!useCase.humanOversight[c.field]} />
                <span>{c.label}</span>
              </div>
            ))}
          </div>
        )}
        <LabelValue label="Accountable person" value={personName(useCase.humanOversight.accountablePersonId)} />
      </SectionCard>
    </div>
  );
};

export default Step9Review;
